/** Shared cell-value helpers for the CRM Lists grid (extracted from CrmListGrid so the filter-tree
 * evaluator can reuse them without a circular import). A "cell value" is a heterogeneous value_json
 * shape: scalar (string/number), a person/org ref object, or a list of either. */
import { isRef, refLabel } from './EntityRefs';

export { isRef };

/** A stable display/search string for any cell value (scalar / list / person|org ref).
 *
 * ⚠ A ref contributes its governed NAME or nothing — never its email, never its id. This feeds
 * SORTING, quick-filter and the review/inspect overlays, so the old `name || email || '#'+id`
 * fallback put a withheld target's id into a sort key and an email into a search index, on surfaces
 * nobody thinks of as "rendering a link". Sorting by an identifier the viewer was refused was never
 * meaningful; a withheld ref now sorts blank. Contract: see `EntityRefs`. */
export function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (isRef(v)) return refLabel(v) ?? '';
  if (Array.isArray(v)) return v.map(cellText).filter((s) => s !== '').join(', ');
  return String(v);
}

/** A cell's value(s) as searchable strings: array cells → one entry per element; scalar → one. */
export function cellValues(v: unknown): string[] {
  return Array.isArray(v) ? v.map(cellText) : [cellText(v)];
}

/** A cell as a finite number, or null if it isn't a scalar number (for numeric filter comparators). */
export function cellNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
